"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableTreemap = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var tail_1 = tslib_1.__importDefault(require("lodash/tail"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var react_dom_1 = require("react-dom");
var bucket_1 = require("../../../constants/bucket");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var uiConfig_1 = require("../../../constants/uiConfig");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var treemapUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/treemapUiConfigHelper");
var TreeMapConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/TreeMapConfigurationPanel"));
var PluggableBaseChart_1 = require("../baseChart/PluggableBaseChart");
var drillDownUtil_1 = require("../drillDownUtil");
var PluggableTreemap = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableTreemap, _super);
    function PluggableTreemap(props) {
        var _this = _super.call(this, props) || this;
        _this.type = sdk_ui_1.VisualizationTypes.TREEMAP;
        _this.supportedPropertiesList = supportedProperties_1.TREEMAP_SUPPORTED_PROPERTIES;
        _this.initializeProperties(props.visualizationProperties);
        return _this;
    }
    PluggableTreemap.prototype.getBucketItemsWithMultipleDates = function (newReferencePoint) {
        var _a;
        var buckets = (_a = newReferencePoint === null || newReferencePoint === void 0 ? void 0 : newReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var stacks = bucketHelper_1.getStackItems(buckets, [bucket_1.ATTRIBUTE, bucket_1.DATE]);
        var nonStackAttributes = bucketHelper_1.getAttributeItemsWithoutStacks(buckets, [bucket_1.ATTRIBUTE, bucket_1.DATE]);
        var view = nonStackAttributes.slice(0, 1);
        if (nonStackAttributes.length > 0) {
            measures = bucketHelper_1.getMeasureItems(bucketHelper_1.limitNumberOfMeasuresInBuckets(buckets, 1));
        }
        if (nonStackAttributes.length > 1 && isEmpty_1.default(stacks)) {
            // first attribute is taken, find next available and put to stacks
            var attributesWithoutFirst = tail_1.default(nonStackAttributes);
            stacks = attributesWithoutFirst.slice(0, 1);
        }
        return { measures: measures, view: view, stacks: stacks };
    };
    PluggableTreemap.prototype.getBucketItems = function (newReferencePoint) {
        var _a;
        var buckets = (_a = newReferencePoint === null || newReferencePoint === void 0 ? void 0 : newReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var stacks = bucketHelper_1.getStackItems(buckets);
        var nonStackAttributes = bucketHelper_1.getAttributeItemsWithoutStacks(buckets);
        var view = nonStackAttributes.slice(0, 1);
        if (nonStackAttributes.length > 0) {
            measures = bucketHelper_1.getMeasureItems(bucketHelper_1.limitNumberOfMeasuresInBuckets(buckets, 1));
        }
        if (nonStackAttributes.length > 1 && isEmpty_1.default(stacks)) {
            // first attribute is taken, find next available non-date attribute
            var attributesWithoutFirst = tail_1.default(nonStackAttributes);
            var nonDate = attributesWithoutFirst.filter(function (attribute) { return !bucketHelper_1.isDateBucketItem(attribute); });
            stacks = nonDate.slice(0, 1);
        }
        return { measures: measures, view: view, stacks: stacks };
    };
    PluggableTreemap.prototype.configureBuckets = function (newReferencePoint) {
        var _a = this.isMultipleDatesEnabled()
            ? this.getBucketItemsWithMultipleDates(newReferencePoint)
            : this.getBucketItems(newReferencePoint), measures = _a.measures, view = _a.view, stacks = _a.stacks;
        set_1.default(newReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: measures,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.VIEW,
                items: view,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.SEGMENT,
                items: stacks,
            },
        ]);
    };
    PluggableTreemap.prototype.getTreemapUIConfig = function (referencePoint) {
        var buckets = referencePoint === null || referencePoint === void 0 ? void 0 : referencePoint.buckets;
        var allowsMultipleDates = this.isMultipleDatesEnabled();
        var nonStackAttributes = allowsMultipleDates
            ? bucketHelper_1.getAttributeItemsWithoutStacks(buckets, [bucket_1.ATTRIBUTE, bucket_1.DATE])
            : bucketHelper_1.getAttributeItemsWithoutStacks(buckets);
        var measures = bucketHelper_1.getMeasureItems(buckets);
        return uiConfig_1.getTreemapUiConfig(allowsMultipleDates, nonStackAttributes.length > 0, measures.length > 1);
    };
    PluggableTreemap.prototype.getExtendedReferencePoint = function (referencePoint) {
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { uiConfig: this.getTreemapUIConfig(referencePoint) });
        newReferencePoint = bucketHelper_1.removeAllArithmeticMeasuresFromDerived(newReferencePoint);
        newReferencePoint = bucketHelper_1.removeAllDerivedMeasures(newReferencePoint);
        this.configureBuckets(newReferencePoint);
        newReferencePoint = treemapUiConfigHelper_1.setTreemapUiConfig(newReferencePoint, this.intl, this.type);
        newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, false);
        newReferencePoint = bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        newReferencePoint = sort_1.removeSort(newReferencePoint);
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggableTreemap.prototype.addFilters = function (source, drillConfig, event) {
        var cutIntersection = drillDownUtil_1.reverseAndTrimIntersection(drillConfig, event.drillContext.intersection);
        return drillDownUtil_1.addIntersectionFiltersToInsight(source, cutIntersection);
    };
    PluggableTreemap.prototype.getInsightWithDrillDownApplied = function (source, drillDownContext) {
        var withFilters = this.addFilters(source, drillDownContext.drillDefinition, drillDownContext.event);
        return drillDownUtil_1.modifyBucketsAttributesForDrillDown(withFilters, drillDownContext.drillDefinition);
    };
    PluggableTreemap.prototype.renderConfigurationPanel = function (insight) {
        if (document.querySelector(this.configPanelElement)) {
            react_dom_1.render(react_1.default.createElement(TreeMapConfigurationPanel_1.default, { locale: this.locale, references: this.references, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, colors: this.colors, pushData: this.handlePushData, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags }), document.querySelector(this.configPanelElement));
        }
    };
    return PluggableTreemap;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggableTreemap = PluggableTreemap;
//# sourceMappingURL=PluggableTreemap.js.map